<script>
    import { onMount } from 'svelte';
    import ActiveStudentDateFilter from './ActiveStudentDateFilter.svelte';
    import { getApiBase } from './apiBase.js';
    import {
        filterRecipients,
        selectedRecipients,
        selectVisible,
        toggleSelection,
    } from './recipientSelection.js';

    // State variables
    let users = [];
    let dateStudents = [];
    let inactiveMonthParents = [];
    let inactiveMonthParentsGrade12OrBelow = [];
    let filteredUsers = [];
    let selectedUserIds = new Set();
    let isLoading = false;
    let isSending = false;
    let errorMessage = '';
    let successMessage = '';
    let searchQuery = '';
    
    // Filter state
    let activeFilter = 'all'; // 'all', 'students', 'teachers', 'student_spreadsheet'
    let emailTarget = 'student';
    
    // Email form data
    let emailSubject = '';
    let emailMessage = '';
    
    // Email progress logging
    let successLogs = [];
    let failedEmails = [];
    let showProgressBox = false;
    
    // Email editing
    let userEmailOverrides = new Map(); // Map of user_id -> custom_email
    let editingEmailUserId = null;
    
    // Student spreadsheet emails
    let spreadsheetEmails = [];
    let spreadsheetEmailsLoaded = false;

    const apiBase = getApiBase();
    
    // Load users on component mount
    onMount(() => {
        loadUsers();
    });
    
    async function loadUsers() {
        isLoading = true;
        errorMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/users-for-email-selection/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to load users');
            }
            
            const data = await response.json();
            users = data.users || [];
            inactiveMonthParents = data.inactive_month_parents || [];
            inactiveMonthParentsGrade12OrBelow = data.inactive_month_parents_grade_12_or_below || [];
            applyFilters();
            
        } catch (error) {
            errorMessage = `Error loading users: ${error.message}`;
            console.error('Error loading users:', error);
        } finally {
            isLoading = false;
        }
    }
    
    function getBaseListForFilter() {
        if (activeFilter === 'active_date_students' || activeFilter === 'inactive_date_students') return dateStudents;
        if (activeFilter === 'inactive_month_parents') {
            return [...inactiveMonthParents];
        }
        if (activeFilter === 'inactive_month_parents_grade_12_or_below') {
            return [...inactiveMonthParentsGrade12OrBelow];
        }
        if (activeFilter === 'student_spreadsheet') return [];

        let recipients = [...users];
        if (activeFilter === 'students') {
            recipients = recipients.filter(user => user.user_type === 'student');
        } else if (activeFilter === 'teachers') {
            recipients = recipients.filter(user => user.user_type === 'teacher');
        }
        return recipients;
    }

    function userSearchText(user) {
        return [
            user.first_name,
            user.last_name,
            user.email,
            user.username,
            getUserEmail(user),
        ].filter(Boolean).join(' ').toLowerCase();
    }

    function applyFilters() {
        filteredUsers = filterRecipients(getBaseListForFilter(), searchQuery, userSearchText);
    }
    
    function setFilter(filter) {
        if (filter !== activeFilter) {
            emailTarget = ['inactive_month_parents', 'inactive_month_parents_grade_12_or_below'].includes(filter) ? 'parent' : 'student';
            userEmailOverrides = new Map();
            editingEmailUserId = null;
        }
        if (filter !== activeFilter) dateStudents = [];
        if (filter !== activeFilter) selectedUserIds = new Set();
        activeFilter = filter;
        if (filter === 'student_spreadsheet' && !spreadsheetEmailsLoaded) {
            loadSpreadsheetEmails();
        }
        applyFilters();
    }

    function updateDateRecipients(event) {
        dateStudents = event.detail;
        selectedUserIds = new Set();
        applyFilters();
    }
    
    async function loadSpreadsheetEmails() {
        isLoading = true;
        errorMessage = '';
        
        const token = localStorage.getItem('jwtToken');
        
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/student-spreadsheet-emails/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to load spreadsheet emails');
            }
            
            const data = await response.json();
            spreadsheetEmails = data.emails || [];
            spreadsheetEmailsLoaded = true;
            
            // Auto-select all spreadsheet emails
            selectedUserIds = new Set(spreadsheetEmails.map((_, index) => `spreadsheet_${index}`));
            
        } catch (error) {
            errorMessage = `Error loading spreadsheet emails: ${error.message}`;
            console.error('Error loading spreadsheet emails:', error);
        } finally {
            isLoading = false;
        }
    }
    
    function toggleUserSelection(userId) {
        selectedUserIds = toggleSelection(selectedUserIds, userId);
    }
    
    function selectAll() {
        selectedUserIds = selectVisible(selectedUserIds, filteredUsers);
    }
    
    function clearAll() {
        selectedUserIds = new Set();
    }
    
    function getSelectedUsers() {
        return selectedRecipients(getBaseListForFilter(), selectedUserIds);
    }
    
    function getUserEmail(user) {
        const destination = emailTarget === 'parent'
            ? (user.parent_email ?? (user.user_type === 'parent' ? user.email : ''))
            : (user.student_email ?? (user.user_type === 'parent' ? '' : user.email));
        return userEmailOverrides.get(user.id) || destination;
    }

    function changeEmailTarget() {
        clearAll();
        userEmailOverrides = new Map();
        editingEmailUserId = null;
        applyFilters();
    }
    
    function startEditingEmail(userId) {
        editingEmailUserId = userId;
    }
    
    function saveEmailEdit(userId, newEmail) {
        if (newEmail.trim()) {
            userEmailOverrides.set(userId, newEmail.trim());
            userEmailOverrides = userEmailOverrides; // Trigger reactivity
        } else {
            userEmailOverrides.delete(userId);
            userEmailOverrides = userEmailOverrides; // Trigger reactivity
        }
        editingEmailUserId = null;
    }
    
    function cancelEmailEdit() {
        editingEmailUserId = null;
    }
    
    function resetEmailOverride(userId) {
        userEmailOverrides.delete(userId);
        userEmailOverrides = userEmailOverrides; // Trigger reactivity
    }
    
    // Custom focus directive
    function focusInput(element) {
        setTimeout(() => element.focus(), 10);
    }
    
    function addSuccessLog(emailAddress) {
        successLogs = [...successLogs, { 
            email: emailAddress, 
            timestamp: new Date() 
        }];
        // Auto-scroll to bottom of progress box
        setTimeout(() => {
            const progressBox = document.querySelector('.progress-logs');
            if (progressBox) {
                progressBox.scrollTop = progressBox.scrollHeight;
            }
        }, 10);
    }
    
    async function sendEmailsOneByOne() {
        if (activeFilter === 'student_spreadsheet' && spreadsheetEmails.length === 0) {
            errorMessage = 'No emails found in student spreadsheet';
            return;
        } else if (activeFilter !== 'student_spreadsheet' && selectedUserIds.size === 0) {
            errorMessage = 'Please select at least one user';
            return;
        }
        
        if (!emailSubject.trim()) {
            errorMessage = 'Email subject is required';
            return;
        }
        
        if (!emailMessage.trim()) {
            errorMessage = 'Email message is required';
            return;
        }
        
        isSending = true;
        showProgressBox = true;
        errorMessage = '';
        successMessage = '';
        successLogs = [];
        failedEmails = [];
        
        const token = localStorage.getItem('jwtToken');
        
        let successCount = 0;
        let failureCount = 0;
        const attemptedEmailAddresses = new Set();

        function shouldSendTo(emailAddress) {
            const normalizedEmail = (emailAddress || '').trim().toLowerCase();
            if (!normalizedEmail || attemptedEmailAddresses.has(normalizedEmail)) {
                return false;
            }
            attemptedEmailAddresses.add(normalizedEmail);
            return true;
        }
        
        // Handle spreadsheet mode differently
        if (activeFilter === 'student_spreadsheet') {
            // Send to all spreadsheet emails directly
            for (const email of spreadsheetEmails) {
                if (!email) {
                    failedEmails.push('Empty email address from spreadsheet');
                    failureCount++;
                    continue;
                }

                if (!shouldSendTo(email)) {
                    continue;
                }
                
                try {
                    const response = await fetch(`${apiBase}/studentplayground/api/send-bulk-emails/`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            user_ids: [], // Empty user IDs for spreadsheet mode
                            subject: emailSubject.trim(),
                            message: emailMessage.trim(),
                            custom_email: email
                        })
                    });
                    
                    const data = await response.json();
                    
                    if (response.ok && data.successful_sends > 0) {
                        addSuccessLog(email);
                        successCount++;
                    } else {
                        failedEmails.push(email);
                        failureCount++;
                    }
                    
                    // Small delay between sends to avoid overwhelming the server
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                } catch (error) {
                    failedEmails.push(email);
                    failureCount++;
                    console.error(`Error sending email to ${email}:`, error);
                }
            }
        } else {
            // Handle regular user mode
            const target = emailTarget;
            const selectedUsers = getSelectedUsers().map(user => ({
                user,
                emailToUse: getUserEmail(user),
                customEmail: userEmailOverrides.get(user.id) || null,
            }));
            
            for (const { user, emailToUse, customEmail } of selectedUsers) {
                if (!emailToUse) {
                    failedEmails.push(`${user.first_name} ${user.last_name} (no email address)`);
                    failureCount++;
                    continue;
                }

                if (!shouldSendTo(emailToUse)) {
                    continue;
                }
            
            try {
                const response = await fetch(`${apiBase}/studentplayground/api/send-bulk-emails/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        user_ids: user.user_type === 'parent' ? [] : [user.id],
                        email_target: target,
                        subject: emailSubject.trim(),
                        message: emailMessage.trim(),
                        custom_email: user.user_type === 'parent'
                            ? emailToUse
                            : customEmail
                    })
                });
                
                const data = await response.json();
                
                if (response.ok && data.successful_sends > 0) {
                    addSuccessLog(emailToUse);
                    successCount++;
                } else {
                    failedEmails.push(emailToUse);
                    failureCount++;
                }
                
                // Small delay between sends to avoid overwhelming the server
                await new Promise(resolve => setTimeout(resolve, 100));
                
            } catch (error) {
                failedEmails.push(emailToUse);
                failureCount++;
                console.error(`Error sending email to ${emailToUse}:`, error);
            }
            }
        }
        
        // Final processing completed
        
        isSending = false;
        
        // Set success message
        if (successCount > 0) {
            successMessage = `Successfully sent ${successCount} emails. ${failureCount > 0 ? `${failureCount} failed.` : ''}`;
        }
        
        // Clear form on success
        if (successCount > 0) {
            emailSubject = '';
            emailMessage = '';
            selectedUserIds = new Set();
        }
    }
    
    // Keep the old function for backward compatibility, but make it call the new one
    async function sendEmails() {
        return sendEmailsOneByOne();
    }
    
    // Reactive statements - trigger filtering when any relevant variable changes
    $: searchQuery, activeFilter, emailTarget, users, inactiveMonthParents, inactiveMonthParentsGrade12OrBelow, userEmailOverrides, applyFilters();
</script>

<div class="select-users-email-container">
    <h3>Send Email to Selected Users</h3>
    
    <!-- Filter buttons -->
    <div class="filter-buttons">
        <button class="filter-btn {activeFilter === 'active_date_students' ? 'active' : ''}" on:click={() => setFilter('active_date_students')}>
            Active Students by Date ({activeFilter === 'active_date_students' ? dateStudents.length : 0})
        </button>
        <button class="filter-btn {activeFilter === 'inactive_date_students' ? 'active' : ''}" on:click={() => setFilter('inactive_date_students')}>
            Inactive Students by Date ({activeFilter === 'inactive_date_students' ? dateStudents.length : 0})
        </button>
        <button 
            class="filter-btn {activeFilter === 'all' ? 'active' : ''}"
            on:click={() => setFilter('all')}
        >
            All Users ({users.length})
        </button>
        <button 
            class="filter-btn {activeFilter === 'students' ? 'active' : ''}"
            on:click={() => setFilter('students')}
        >
            Students ({users.filter(u => u.user_type === 'student').length})
        </button>
        <button 
            class="filter-btn {activeFilter === 'teachers' ? 'active' : ''}"
            on:click={() => setFilter('teachers')}
        >
            Teachers ({users.filter(u => u.user_type === 'teacher').length})
        </button>
        <button
            class="filter-btn {activeFilter === 'inactive_month_parents' ? 'active' : ''}"
            on:click={() => setFilter('inactive_month_parents')}
        >
            Inactive This Month Parents ({inactiveMonthParents.length})
        </button>
        <button
            class="filter-btn {activeFilter === 'inactive_month_parents_grade_12_or_below' ? 'active' : ''}"
            on:click={() => setFilter('inactive_month_parents_grade_12_or_below')}
        >
            Inactive Parents Grade 12 or Below ({inactiveMonthParentsGrade12OrBelow.length})
        </button>
        <button 
            class="filter-btn {activeFilter === 'student_spreadsheet' ? 'active' : ''}"
            on:click={() => setFilter('student_spreadsheet')}
        >
            Student Spreadsheet ({spreadsheetEmailsLoaded ? spreadsheetEmails.length : '...'})
        </button>
    </div>
    
    {#if activeFilter !== 'student_spreadsheet'}
        <div class="form-group">
            <label for="email-recipient-target">Send to</label>
            <select id="email-recipient-target" class="form-input" bind:value={emailTarget} on:change={changeEmailTarget} disabled={isSending}>
                <option value="student">{activeFilter === 'teachers' ? 'Teacher email' : 'Student email'}</option>
                <option value="parent">Parent email</option>
            </select>
            <p>Only the selected email is used. Missing addresses are reported, and shared addresses receive one message.</p>
        </div>
    {/if}

    {#if activeFilter === 'active_date_students' || activeFilter === 'inactive_date_students'}
        {#key activeFilter}
            <ActiveStudentDateFilter channel="email" inactive={activeFilter === 'inactive_date_students'} on:recipients={updateDateRecipients} />
        {/key}
    {/if}

    <!-- Search bar -->
    <div class="search-container">
        <input 
            type="text" 
            placeholder="Search by name, email, or username..." 
            bind:value={searchQuery}
            class="search-input"
        />
    </div>
    
    <!-- Selection controls -->
    <div class="selection-controls">
        <button on:click={selectAll} class="selection-btn">
            Select All Visible ({filteredUsers.length})
        </button>
        <button on:click={clearAll} class="selection-btn">
            Clear All
        </button>
        <span class="selected-count">
            Selected: {selectedUserIds.size} users
        </span>
    </div>
    
    <!-- Users list -->
    {#if isLoading}
        <div class="loading">Loading {activeFilter === 'student_spreadsheet' ? 'spreadsheet emails' : 'users'}...</div>
    {:else if activeFilter === 'student_spreadsheet'}
        {#if spreadsheetEmails.length === 0}
            <div class="no-users">No emails found in student spreadsheet</div>
        {:else}
            <div class="spreadsheet-info">
                <p><strong>{spreadsheetEmails.length} unique billing emails found in student spreadsheet</strong></p>
                <p>All emails have been automatically selected for sending.</p>
            </div>
            <div class="users-list">
                {#each spreadsheetEmails as email, index (`spreadsheet_${index}`)}
                    <div class="user-item selected">
                        <label class="user-label">
                            <input 
                                type="checkbox" 
                                checked={true}
                                disabled={true}
                            />
                            <div class="user-info">
                                <div class="user-name">
                                    Spreadsheet Email #{index + 1}
                                    <span class="user-type spreadsheet">spreadsheet</span>
                                </div>
                                <div class="user-email-container">
                                    <div class="user-email-display">
                                        <span class="user-email">{email}</span>
                                    </div>
                                </div>
                            </div>
                        </label>
                    </div>
                {/each}
            </div>
        {/if}
    {:else if filteredUsers.length === 0}
        <div class="no-users">No users found</div>
    {:else}
        <div class="users-list">
            {#each filteredUsers as user (user.id)}
                <div class="user-item {selectedUserIds.has(user.id) ? 'selected' : ''}">
                    <label class="user-label">
                        <input 
                            type="checkbox" 
                            checked={selectedUserIds.has(user.id)}
                            on:change={() => toggleUserSelection(user.id)}
                        />
                        <div class="user-info">
                            <div class="user-name">
                                {user.first_name} {user.last_name}
                                <span class="user-type {user.user_type}">{user.user_type}</span>
                            </div>
                            <div class="user-email-container">
                                {#if editingEmailUserId === user.id}
                                    <div class="email-edit-container">
                                        <input 
                                            type="email" 
                                            value={getUserEmail(user)}
                                            class="email-edit-input"
                                            placeholder="Enter email address"
                                            on:keydown={(e) => {
                                                if (e.key === 'Enter') {
                                                    saveEmailEdit(user.id, e.target.value);
                                                } else if (e.key === 'Escape') {
                                                    cancelEmailEdit();
                                                }
                                            }}
                                            on:blur={(e) => saveEmailEdit(user.id, e.target.value)}
                                            use:focusInput
                                        />
                                        <button 
                                            class="email-edit-btn save"
                                            on:click={(e) => {
                                                e.stopPropagation();
                                                const input = e.target.parentElement.querySelector('input');
                                                saveEmailEdit(user.id, input.value);
                                            }}
                                        >
                                            ✓
                                        </button>
                                        <button 
                                            class="email-edit-btn cancel"
                                            on:click={(e) => {
                                                e.stopPropagation();
                                                cancelEmailEdit();
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                {:else}
                                    <div class="user-email-display">
                                        <span class="user-email {userEmailOverrides.has(user.id) ? 'custom-email' : ''}">
                                            {getUserEmail(user) || (emailTarget === 'parent' ? 'No parent email' : 'No student email')}
                                            {#if userEmailOverrides.has(user.id)}
                                                <span class="email-status">(custom)</span>
                                            {/if}
                                        </span>
                                        <button 
                                            class="edit-email-btn"
                                            on:click={(e) => {
                                                e.stopPropagation();
                                                startEditingEmail(user.id);
                                            }}
                                            title="Edit email address"
                                        >
                                            ✏️
                                        </button>
                                        {#if userEmailOverrides.has(user.id)}
                                            <button 
                                                class="reset-email-btn"
                                                on:click={(e) => {
                                                    e.stopPropagation();
                                                    resetEmailOverride(user.id);
                                                }}
                                                title="Reset to original email"
                                            >
                                                ↺
                                            </button>
                                        {/if}
                                    </div>
                                {/if}
                            </div>
                        </div>
                    </label>
                </div>
            {/each}
        </div>
    {/if}
    
    <!-- Email form -->
    {#if selectedUserIds.size > 0}
        <div class="email-form">
            <h4>Email Details</h4>
            
            <div class="form-group">
                <label for="email-subject">Subject:</label>
                <input 
                    id="email-subject"
                    type="text" 
                    bind:value={emailSubject}
                    placeholder="Enter email subject"
                    class="form-input"
                />
            </div>
            
            <div class="form-group">
                <label for="email-message">Message:</label>
                <textarea 
                    id="email-message"
                    bind:value={emailMessage}
                    placeholder="Enter email message"
                    rows="8"
                    class="form-textarea"
                ></textarea>
            </div>
            
            <div class="selected-preview">
                {#if activeFilter === 'student_spreadsheet'}
                    <h5>Sending to {spreadsheetEmails.length} spreadsheet emails:</h5>
                    <div class="preview-list">
                        {#each spreadsheetEmails as email}
                            <span class="preview-user">Spreadsheet Email ({email})</span>
                        {/each}
                    </div>
                {:else}
                    <h5>Sending to {selectedUserIds.size} users:</h5>
                    <div class="preview-list">
                        {#each getSelectedUsers() as user}
                            <span class="preview-user">
                                {user.first_name} {user.last_name} ({getUserEmail(user)})
                                {#if userEmailOverrides.has(user.id)}
                                    <span class="custom-indicator">*</span>
                                {/if}
                            </span>
                        {/each}
                    </div>
                {/if}
            </div>
            
            <button 
                on:click={sendEmails}
                disabled={isSending || (activeFilter === 'student_spreadsheet' ? spreadsheetEmails.length === 0 : selectedUserIds.size === 0)}
                class="send-btn {isSending ? 'sending' : ''}"
            >
                {#if isSending}
                    Sending...
                {:else}
                    Send Emails
                {/if}
            </button>
        </div>
    {/if}
    
    <!-- Email Progress Display -->
    {#if showProgressBox && (successLogs.length > 0 || isSending)}
        <div class="progress-container">
            <div class="progress-header">
                <h4>Email Sending Progress</h4>
                <button on:click={() => showProgressBox = false} class="close-progress">×</button>
            </div>
            <div class="progress-logs">
                {#if isSending && successLogs.length === 0}
                    <div class="progress-message">Starting email sending process...</div>
                {/if}
                {#each successLogs as log}
                    <div class="success-entry">
                        <span class="timestamp">[{log.timestamp.toLocaleTimeString()}]</span>
                        <span class="success-message">Email SUCCESSFULLY sent to: {log.email}</span>
                    </div>
                {/each}
                {#if !isSending && successLogs.length > 0}
                    <div class="completion-message">
                        Email sending completed. {successLogs.length} emails sent successfully.
                    </div>
                {/if}
            </div>
        </div>
    {/if}
    
    <!-- Final failed emails summary -->
    {#if !isSending && failedEmails.length > 0 && showProgressBox}
        <div class="failed-summary">
            <h4>Failed Email Summary</h4>
            <p>The following email addresses failed to receive the email:</p>
            <ul class="failed-list">
                {#each failedEmails as email}
                    <li>{email}</li>
                {/each}
            </ul>
            <p class="contact-note">Please contact Herkus for assistance with these failed emails.</p>
        </div>
    {/if}
    
    <!-- Messages -->
    {#if errorMessage}
        <div class="error-message">{errorMessage}</div>
    {/if}
    
    {#if successMessage}
        <div class="success-message">{successMessage}</div>
    {/if}
</div>

<style>
    .select-users-email-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
    }
    
    .filter-buttons {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }
    
    .filter-btn {
        padding: 8px 16px;
        border: 1px solid #ddd;
        background: white;
        cursor: pointer;
        border-radius: 6px;
        font-size: 14px;
        transition: all 0.2s ease;
    }
    
    .filter-btn.active {
        background: #fab45a;
        color: black;
        border-color: #fab45a;
    }
    
    .filter-btn:hover:not(.active) {
        background: #f5f5f5;
    }
    
    .search-container {
        margin-bottom: 15px;
    }
    
    .search-input {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
    }
    
    .selection-controls {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }
    
    .selection-btn {
        padding: 6px 12px;
        border: 1px solid #007bff;
        background: white;
        color: #007bff;
        cursor: pointer;
        border-radius: 4px;
        font-size: 13px;
        transition: all 0.2s ease;
    }
    
    .selection-btn:hover {
        background: #007bff;
        color: white;
    }
    
    .selected-count {
        font-weight: bold;
        color: #555;
        margin-left: auto;
    }
    
    .loading, .no-users {
        text-align: center;
        padding: 20px;
        color: #666;
        font-style: italic;
    }
    
    .users-list {
        max-height: 400px;
        overflow-y: auto;
        border: 1px solid #ddd;
        border-radius: 6px;
        margin-bottom: 20px;
    }
    
    .user-item {
        border-bottom: 1px solid #eee;
        transition: background-color 0.2s ease;
    }
    
    .user-item:last-child {
        border-bottom: none;
    }
    
    .user-item.selected {
        background-color: #f0f8ff;
    }
    
    .user-item:hover {
        background-color: #f9f9f9;
    }
    
    .user-item.selected:hover {
        background-color: #e6f3ff;
    }
    
    .user-label {
        display: flex;
        align-items: center;
        padding: 12px;
        cursor: pointer;
        gap: 12px;
    }
    
    .user-info {
        flex: 1;
    }
    
    .user-name {
        font-weight: 500;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    
    .user-type {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
    }
    
    .user-type.student {
        background-color: #e3f2fd;
        color: #1976d2;
    }
    
    .user-type.teacher {
        background-color: #f3e5f5;
        color: #7b1fa2;
    }
    
    .user-type.spreadsheet {
        background-color: #fff3cd;
        color: #856404;
    }
    
    .spreadsheet-info {
        background: #e3f2fd;
        padding: 15px;
        border-radius: 6px;
        margin-bottom: 15px;
        border: 1px solid #90caf9;
    }
    
    .spreadsheet-info p {
        margin: 5px 0;
        color: #1565c0;
    }
    
    .spreadsheet-info strong {
        color: #0d47a1;
    }
    
    .user-email-container {
        flex: 1;
    }
    
    .user-email-display {
        display: flex;
        align-items: center;
        gap: 6px;
    }
    
    .user-email {
        color: #666;
        font-size: 13px;
        flex: 1;
    }
    
    .user-email.custom-email {
        color: #0066cc;
        font-weight: 500;
    }
    
    .email-status {
        font-size: 11px;
        color: #0066cc;
        font-style: italic;
    }
    
    .edit-email-btn, .reset-email-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 2px;
        font-size: 12px;
        opacity: 0.6;
        transition: opacity 0.2s ease;
        border-radius: 3px;
    }
    
    .edit-email-btn:hover, .reset-email-btn:hover {
        opacity: 1;
        background-color: #f0f0f0;
    }
    
    .reset-email-btn {
        color: #666;
        font-weight: bold;
    }
    
    .email-edit-container {
        display: flex;
        align-items: center;
        gap: 4px;
        width: 100%;
    }
    
    .email-edit-input {
        flex: 1;
        padding: 4px 6px;
        border: 1px solid #ddd;
        border-radius: 3px;
        font-size: 12px;
        min-width: 0;
    }
    
    .email-edit-input:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
    
    .email-edit-btn {
        background: none;
        border: 1px solid #ddd;
        cursor: pointer;
        padding: 2px 6px;
        font-size: 11px;
        border-radius: 3px;
        min-width: 20px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
    }
    
    .email-edit-btn.save {
        color: #28a745;
        border-color: #28a745;
    }
    
    .email-edit-btn.save:hover {
        background-color: #28a745;
        color: white;
    }
    
    .email-edit-btn.cancel {
        color: #dc3545;
        border-color: #dc3545;
    }
    
    .email-edit-btn.cancel:hover {
        background-color: #dc3545;
        color: white;
    }
    
    .custom-indicator {
        color: #0066cc;
        font-weight: bold;
        margin-left: 4px;
    }
    
    .email-form {
        background: #f9f9f9;
        padding: 20px;
        border-radius: 8px;
        margin-top: 20px;
    }
    
    .form-group {
        margin-bottom: 15px;
    }
    
    .form-group label {
        display: block;
        margin-bottom: 5px;
        font-weight: 500;
        color: #333;
    }
    
    .form-input, .form-textarea {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
    }
    
    .form-textarea {
        resize: vertical;
        min-height: 120px;
    }
    
    .selected-preview {
        margin-bottom: 20px;
        padding: 15px;
        background: white;
        border-radius: 6px;
        border: 1px solid #ddd;
    }
    
    .selected-preview h5 {
        margin: 0 0 10px 0;
        color: #333;
    }
    
    .preview-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    
    .preview-user {
        background: #e9ecef;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        color: #495057;
    }
    
    .send-btn {
        padding: 12px 24px;
        background: #28a745;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .send-btn:hover:not(:disabled) {
        background: #218838;
    }
    
    .send-btn:disabled {
        background: #6c757d;
        cursor: not-allowed;
    }
    
    .send-btn.sending {
        background: #ffc107;
        color: #212529;
    }
    
    .error-message {
        background: #f8d7da;
        color: #721c24;
        padding: 12px;
        border-radius: 6px;
        margin-top: 15px;
        border: 1px solid #f5c6cb;
    }
    
    .success-message {
        background: #d4edda;
        color: #155724;
        padding: 12px;
        border-radius: 6px;
        margin-top: 15px;
        border: 1px solid #c3e6cb;
    }
    
    /* Progress Box styles */
    .progress-container {
        margin-top: 20px;
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    
    .progress-header {
        background: #f8f9fa;
        color: #333;
        padding: 12px 15px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #e0e0e0;
    }
    
    .progress-header h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 500;
        color: #333;
    }
    
    .close-progress {
        background: none;
        border: none;
        color: #666;
        font-size: 18px;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
        transition: all 0.2s ease;
    }
    
    .close-progress:hover {
        background: #e9ecef;
        color: #333;
    }
    
    .progress-logs {
        height: 250px;
        overflow-y: auto;
        padding: 15px;
        background: white;
        font-size: 13px;
        line-height: 1.5;
    }
    
    .progress-message {
        color: #6c757d;
        font-style: italic;
        margin-bottom: 10px;
    }
    
    .success-entry {
        margin-bottom: 8px;
        display: flex;
        align-items: flex-start;
        word-wrap: break-word;
        padding: 4px 0;
    }
    
    .success-message {
        color: #28a745;
        font-weight: 500;
        flex: 1;
    }
    
    .completion-message {
        margin-top: 15px;
        padding: 10px;
        background: #d4edda;
        color: #155724;
        border-radius: 4px;
        font-weight: 500;
        text-align: center;
    }
    
    .timestamp {
        color: #6c757d;
        margin-right: 10px;
        flex-shrink: 0;
        font-size: 11px;
        font-family: monospace;
    }
    
    /* Progress scrollbar */
    .progress-logs::-webkit-scrollbar {
        width: 6px;
    }
    
    .progress-logs::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    
    .progress-logs::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 3px;
    }
    
    .progress-logs::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
    }
    
    /* Failed summary styles */
    .failed-summary {
        margin-top: 20px;
        background: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 8px;
        padding: 20px;
        color: #856404;
    }
    
    .failed-summary h4 {
        margin: 0 0 10px 0;
        color: #721c24;
    }
    
    .failed-list {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 15px;
        margin: 15px 0;
        max-height: 200px;
        overflow-y: auto;
    }
    
    .failed-list li {
        margin-bottom: 8px;
        padding: 4px 0;
        border-bottom: 1px solid #f0f0f0;
        font-family: 'Courier New', monospace;
        font-size: 13px;
    }
    
    .failed-list li:last-child {
        border-bottom: none;
        margin-bottom: 0;
    }
    
    .contact-note {
        margin: 15px 0 0 0;
        font-weight: 500;
        font-style: italic;
        color: #495057;
    }
    
    /* Responsive design */
    @media (max-width: 768px) {
        .select-users-email-container {
            padding: 15px;
        }
        
        .filter-buttons, .selection-controls {
            flex-direction: column;
            align-items: stretch;
        }
        
        .selected-count {
            margin-left: 0;
            margin-top: 10px;
        }
        
        .user-label {
            padding: 10px;
        }
        
        .preview-list {
            flex-direction: column;
        }
    }
</style>
